import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { CircularProgress, ButtonGroup, Button, Box } from "@mui/material";

import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { useStore } from "../authContext/StoreContext";
import api from "../hooks/apiClient";
import { useSnackbar } from "notistack";
import PriceTagDialogMulti from "../components/PriceTagDialogMulti";
const Products = ({ authToken }) => {
  const navigate = useNavigate();
  const {
    selectedStore,
    selectedDestStore,
    isStoreSelected,
    isDestStoreSelected,
  } = useStore();

  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sizeFirstPart, setSizeFirstPart] = useState("7-8");
  const [sizeSecPart, setSizeSecPart] = useState("7/8");
  const [filter, setFilter] = useState("active");
  const [selectedRows, setSelectedRows] = useState([]);
  const [viewMode, setViewMode] = useState("source");
  const activeStoreId = isStoreSelected && selectedStore.id;
  const destStoreId = isDestStoreSelected && selectedDestStore.id;
  const [barcodePage, setBarcodePage] = useState(false);
  const [printableProducts, setPrintableProducts] = useState(null);
  const fetchProducts = useCallback(
    async (storeId, status, isDestination = false) => {
      try {
        setLoading(true);
        const endpoint = isDestination
          ? `products/destination/bystore/${storeId}`
          : `products/bystore/${storeId}`;

        const params = new URLSearchParams({ param: status });
        const response = await api.get(`${endpoint}?${params}`, {
          headers: { token: authToken },
        });

        const productsWithQuantity = response.data.map((product) => ({
          ...product,
          totalQuantity: isDestination
            ? product.client_variants?.reduce(
                (acc, v) => acc + v.inventory_quantity,
                0,
              ) || 0
            : product.variants?.reduce(
                (acc, v) => acc + v.inventory_quantity,
                0,
              ) || 0,
        }));

        setData(productsWithQuantity);
        setFilter(status);
        setViewMode(isDestination ? "destination" : "source");
      } catch (error) {
        console.error("Error fetching products:", error);
        enqueueSnackbar(
          `Failed to load ${isDestination ? "destination" : "source"} products`,
          { variant: "error" },
        );
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  useEffect(() => {
    if (isStoreSelected && selectedStore.type === "source") {
      fetchProducts(activeStoreId, "active");
    }
    if (isStoreSelected && selectedStore.type === "destination") {
      fetchProducts(activeStoreId, "active", true);
    }
  }, [activeStoreId, fetchProducts]);

  const MapTheseProducts = async () => {
    setLoading(true);
    if (selectedRows.length == 0 || !isDestStoreSelected) {
      enqueueSnackbar("Products And Store cannot be empty..", {
        variant: "warning",
      });
      setLoading(false);
      return null;
    }

    try {
      const response = await api.post(`products/multi/map`, {
        client_store: destStoreId,
        product_ids: selectedRows,
      });
    } catch (error) {
      enqueueSnackbar("some thing went wrong..", { variant: "error" });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  const PrintPriceTags = async () => {
    try {
      setPrintableProducts(
        selectedRows.map(prepareProductForPrint).filter(Boolean),
      );
      setBarcodePage(true);
    } catch (error) {
      enqueueSnackbar("some thing went wrong..", { variant: "error" });
      console.error(error);
    }
  };
  const isSevenEight = (title = "") =>
    title.includes(sizeFirstPart) || title.includes(sizeSecPart);

  const prepareProductForPrint = (product) => {
    if (!product?.variants?.length) return null;

    // 1️⃣ lowest price from ALL variants
    const lowestPrice = Math.min(
      ...product.variants.map((v) => Number(v.price)).filter((p) => !isNaN(p)),
    );

    // 2️⃣ variant for DATA (7–8)
    const sevenEightVariant = product.variants.find((v) =>
      isSevenEight(v.title),
    );

    if (!sevenEightVariant) return null;

    return {
      ...product,
      lowestPrice,
      variants: [
        {
          ...sevenEightVariant,
          price: lowestPrice, // override price ONLY
          bar_quantity: 1,
        },
      ],
    };
  };

  const columns = [
    {
      field: "image_src", // You can use a field that represents the image URL
      headerName: "Image",
      width: 70,
      renderCell: (params) => (
        <img
          src={params.value} // Assuming params.value contains the image URL
          alt="Product Image"
          style={{ width: 50, height: 50 }}
        />
      ),
    },
    { field: "title", headerName: "Title", width: 500 },
    { field: "id", headerName: "Product ID", width: 150 },
    { field: "status", headerName: "Status", width: 100 },
    { field: "totalQuantity", headerName: "Inventory", width: 150 },
    {
      field: "mapped",
      headerName: "Mapped",
      width: 250,
      renderCell: (params) => (
        <>
          {params.row.mapped &&
            params.row.mapped.split(",").map((value, index) => (
              <span
                key={index}
                type="button"
                className="btn btn-sm btn-warning me-1 mb-1"
                onClick={() =>
                  console.log(`Button clicked for value: ${value}`)
                }
              >
                {value.trim()}{" "}
                {/* Use .trim() to remove leading/trailing spaces */}
              </span>
            ))}
        </>
      ),
    },
    {
      field: "laam_sync",
      headerName: "Laam",
      width: 150,
      renderCell: (params) => (
        <>
          {params.row.laam_sync && (
            <span
              type="button"
              className="btn btn-sm btn-warning me-1 mb-1"
              onClick={() =>
                console.log(`Button clicked for value: ${params.row.laam_sync}`)
              }
            >
              {params.row.laam_sync ? "Laam" : "No"}
            </span>
          )}
        </>
      ),
    },
  ];
  const SyncOneProduct = async () => {
    const userConfirmed = window.confirm(
      "Are you sure you want to sync your product",
    );
    if (userConfirmed) {
      try {
        const response = await api.get(`products/shopify/`, {
          headers: {
            token: authToken,
          },
        });

        enqueueSnackbar(response.data.message, { variant: "success" });
        fetchProducts(activeStoreId, filter);
      } catch (error) {
        console.error("Error making request:", error);

        enqueueSnackbar(error.message, { variant: "error" });
      }
    } else {
      enqueueSnackbar("Void the Operation", { variant: "error" });
    }
  };

  const handleRowClick = (params) => {
    navigate(
      viewMode === "source"
        ? `/product/${params.row.id}`
        : `/products/${params.row.id}`,
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isStoreSelected) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" p={4}>
        <SyncProblemIcon fontSize="large" />
        <p>Please select a store first</p>
      </Box>
    );
  }
  if (barcodePage) {
    return (
      <PriceTagDialogMulti
        printableProducts={printableProducts}
        setBarcodePage={setBarcodePage}
      />
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
          justifyContent: { xs: "flex-start", sm: "space-between" }, // Start-aligned on small, spaced on larger
          flexDirection: { xs: "column", sm: "row" }, // Column on xs, row on sm+
          flexWrap: "wrap", // Keep for extra wrapping if needed
          gap: 2,
        }}
      >
        <ButtonGroup color="warning" size="small" variant="outlined">
          <Button
            onClick={() => fetchProducts(activeStoreId, "active")}
            disabled={filter === "active" && viewMode === "source"}
          >
            Active
          </Button>
          <Button
            onClick={() => fetchProducts(activeStoreId, "all")}
            disabled={filter === "all" && viewMode === "source"}
          >
            All Source
          </Button>
        </ButtonGroup>
        <span className="badge bg-success">
          {isStoreSelected && selectedStore.type === "destination"
            ? `${selectedStore.public_name} Destination Store Selected As main`
            : ""}
        </span>
        {isDestStoreSelected && (
          <Box>
            <ButtonGroup color="secondary" size="small" variant="outlined">
              <Button
                onClick={() => fetchProducts(destStoreId, "active", true)}
                disabled={filter === "active" && viewMode === "destination"}
              >
                Dest Active
              </Button>
              <Button
                onClick={() => fetchProducts(destStoreId, "all", true)}
                disabled={filter === "all" && viewMode === "destination"}
              >
                All Dest
              </Button>
            </ButtonGroup>
            {isStoreSelected && selectedStore.type === "source" && (
              <button
                className="btn btn-sm btn-outline-secondary ms-1"
                onClick={() => MapTheseProducts()}
              >
                Map with
                <span className="badge bg-primary ms-1">
                  {selectedDestStore.public_name}
                  <span className="active ms-1">
                    {selectedDestStore.isActive && "active"}
                  </span>
                </span>
              </button>
            )}
          </Box>
        )}
        {selectedRows.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0, // Prevents this box from shrinking/wrapping
            }}
          >
            <input
              className="form-control"
              type="text"
              value={sizeFirstPart}
              onChange={(e) => setSizeFirstPart(e.target.value)}
              placeholder="First size part"
              style={{ flex: 1 }}
            />
            <input
              className="form-control"
              type="text"
              value={sizeSecPart}
              onChange={(e) => setSizeSecPart(e.target.value)}
              placeholder="Second size part"
              style={{ flex: 1 }}
            />
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => PrintPriceTags()}
              style={{ flexShrink: 0 }}
            >
              Print price tags
            </button>
          </Box>
        )}
      </Box>

      {data.length > 0 && (
        <div style={{ height: 1000, width: "100%" }}>
          <DataGrid
            rows={data}
            columns={columns}
            pageSize={5}
            getRowId={(row) => row.id}
            onRowClick={handleRowClick}
            checkboxSelection
            onRowSelectionModelChange={(ids) => {
              const selectedIDs = new Set(ids);
              const selectedRows = data.filter((row) =>
                selectedIDs.has(row.id),
              );
              setSelectedRows(selectedRows);
            }}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
              },
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default Products;
